import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, Info, Plus, Printer, RotateCcw, Save, Search } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup, clearLookup } from '../HRContext';
import { errorText } from '../../utils';
import RecordForm, { Input } from '../components/Form';
import { RecordDetail } from '../components/Register';
import StatusPill from '../components/StatusPill';
import MiniTable from '../components/MiniTable';
import { RESOURCES } from '../resources';
import { daysUntil, download, fmtDate, fmtDateTime, has, initials, titleCase, toCSV } from '../format';
import { hrSubUrl } from '../catalog';
import Icon from '../Icon';
import Loader from '../components/Loader';
import { Select } from '../components/Controls';

// Classification, immigration, permits and document compliance for expatriate staff —
// the port of Stephanet's Expatriate Classification workspace (custom/ExpatriateClassification.jsx + ExpatTabs.jsx).

const COUNTRIES = ['Nigeria', 'United Kingdom', 'United States', 'India', 'China', 'Lebanon', 'Philippines', 'South Africa', 'Ghana', 'Egypt', 'France',
  'Germany', 'Italy', 'Netherlands', 'Canada', 'Australia', 'Brazil', 'Turkey', 'United Arab Emirates', 'Kenya'];
const FIELDS = {
  is_expatriate: { label: 'Expatriate Employee', type: 'boolean' },
  expat_class: { label: 'Expatriate Class', type: 'select', options: ['Class A', 'Class B', 'Class C', 'Class D'].map(v => ({ value: v, label: v })) },
  is_private: { label: 'Private (Restricted Access)', type: 'boolean' },
  passport_number: { label: 'Passport Number' },
  passport_country: { label: 'Country of Issue', suggestions: COUNTRIES },
  passport_expiry_date: { label: 'Passport Expiry Date', type: 'date' },
  visa_type: { label: 'Visa Type', suggestions: ['STR', 'TWP', 'Business', 'Visitor'] },
  visa_expiry_date: { label: 'Visa / STR Expiry Date', type: 'date' },
  cerpac_number: { label: 'CERPAC Number' },
  cerpac_expiry_date: { label: 'CERPAC Expiry Date', type: 'date' },
  quota_slot_reference: { label: 'Quota Slot / Approval Reference' },
  reports_to_id: { label: 'Reports To (Line Manager)', type: 'ref', ref: 'employees' },
  probation_end_date: { label: 'Probation End Date', type: 'date' },
  confirmation_date: { label: 'Confirmation Date', type: 'date' },
};
Object.entries(FIELDS).forEach(([n, f]) => { f.name = n; });
const EXPAT_KEYS = ['is_expatriate', 'expat_class', 'passport_number', 'passport_country', 'passport_expiry_date', 'visa_type', 'visa_expiry_date', 'cerpac_number', 'cerpac_expiry_date', 'quota_slot_reference'];
const MASTER_KEYS = ['is_private', 'reports_to_id', 'probation_end_date', 'confirmation_date'];

const flatten = e => ({ ...e, ...(e.expatriate_detail || {}), id: e.id, is_expatriate: !!e.expatriate_detail?.is_expatriate });
const same = (a, b) => (a ?? '') === (b ?? '') || String(a ?? '') === String(b ?? '');

// expiry band shared by every register: [label, css band]
export function expiryBand(d) {
  const left = daysUntil(d);
  if (left === null) return ['No Expiry', 'none'];
  if (left < 0) return ['Expired', 'expired'];
  if (left <= 30) return ['Expiring in 30 days', 'd30'];
  if (left <= 90) return ['Expiring in 90 days', 'd90'];
  if (left <= 180) return ['Expiring in 180 days', 'd180'];
  return ['Valid', 'valid'];
}
const Band = ({ date, text }) => { const [l, b] = expiryBand(date); return <span className={`xp-band ${b}`}>{text || l}</span>; };
function ExpiryHint({ date }) {
  const left = daysUntil(date);
  if (left === null) return null;
  const tone = left < 0 ? 'bad' : left <= 90 ? 'warn' : 'ok';
  return <small className={`xp-hint ${tone}`}>{left < 0 ? `Expired ${-left} day${left === -1 ? '' : 's'} ago` : `Expires in ${left} day${left === 1 ? '' : 's'}`}</small>;
}

function Section({ icon, title, hint, actions, children, className = '' }) {
  return (
    <div className={`panel xp-section ${className}`}>
      <div className="toolbar">
        <div className="sec-head">
          {icon && <span className="sec-icon"><Icon name={icon} size={17} /></span>}
          <div><h3 className="panel-title">{title}</h3>{hint && <p className="muted small">{hint}</p>}</div>
        </div>
        <div className="spacer" />{actions}
      </div>
      {children}
    </div>
  );
}
const Note = ({ tone = 'info', children }) => <div className={`xp-note ${tone}`}>{tone === 'info' ? <Info size={16} /> : <AlertTriangle size={16} />}<span>{children}</span></div>;
const Grid = ({ cols = 3, children }) => <div className={`form-grid cols-${cols}`}>{children}</div>;

function Fld({ x, name, help }) {
  const f = FIELDS[name];
  return (
    <div className={`field ${f.full ? 'full' : ''}`}>
      <span className="field-label">{f.label}</span>
      <Input field={f} value={x.e[name]} onChange={v => x.set(name, v)} disabled={!x.canManage} />
      {f.type === 'date' && <ExpiryHint date={x.e[name]} />}
      {help && <small className="muted small">{help}</small>}
    </div>
  );
}
const AddBtn = ({ x, res, label, defaults }) =>
  x.canManage ? <button className="btn primary" onClick={() => x.add(res, { employee_id: x.e.id, ...defaults })}><Plus size={15} /> {label}</button> : null;

// ---------------------------------------------------------------- tabs
function Classification(x) {
  const e = x.e;
  const chips = [[e.is_expatriate ? 'Expatriate Employee' : 'Local Employee', 'blue'], [titleCase(e.employment_type), 'green'],
    [e.is_private ? 'Private Record' : 'Normal Access', 'purple'], [e.expat_class, 'amber']];
  return (
    <>
      <div className="xp-split">
        <Section icon="badge-check" title="Employee Category & Classification" hint="Classify the employee for organizational, operational, and compliance purposes.">
          <Grid cols={2}>
            <Fld x={x} name="is_expatriate" help="Foreign nationals working under quota and residence permits" />
            <Fld x={x} name="expat_class" help="Quota class used for immigration filings" />
            <Fld x={x} name="is_private" help="Limits who can view this record" />
          </Grid>
          {e.is_expatriate ? <Note>Expatriate employee selected. Additional expatriate information sections are now available.</Note>
            : <Note tone="warn">This employee is not classified as an expatriate. Expatriate sections stay available but are optional.</Note>}
        </Section>
        <div className="panel xp-preview">
          <h3 className="panel-title">Quick Preview</h3>
          <div className="xp-photo">{initials(e.full_name)}</div>
          <b className="xp-name">{String(e.full_name || '').toUpperCase()}</b>
          <span className="muted small">{e.designation_title || '—'}</span>
          <span className="mono small">{e.employee_no}</span>
          <div className="xp-chips">{chips.filter(([v]) => v).map(([v, c]) => <span key={v} className={`xp-chip lc-${c}`}><CheckCircle2 size={13} /> {v}</span>)}</div>
        </div>
      </div>
      <Section icon="list-checks" title="Classification Summary">
        <div className="table-wrap">
          <table className="xp-table">
            <thead><tr><th>Employee Type</th><th>Expatriate Class</th><th>Employment Type</th><th>Privacy Status</th><th>Nationality</th><th>Last Updated</th></tr></thead>
            <tbody><tr>
              <td><b>{e.is_expatriate ? 'Expatriate Employee' : 'Local Employee'}</b></td><td>{e.expat_class || '—'}</td><td>{titleCase(e.employment_type)}</td>
              <td>{e.is_private ? 'Private Employee' : 'Normal'}</td><td>{e.personal_info?.nationality || '—'}</td><td>{fmtDateTime(e.updated_at)}</td>
            </tr></tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function DocTable({ x, rows }) {
  if (!rows.length) return <div className="empty">No records on file.</div>;
  return (
    <div className="table-wrap">
      <table className="xp-table">
        <thead><tr><th>Document</th><th>Number</th><th>Issuing Authority</th><th>Issue Date</th><th>Expiry Date</th><th>Days Left</th><th>Status</th><th>Verified</th></tr></thead>
        <tbody>{rows.map(d => (
          <tr key={d.id} className="clickable" onClick={() => x.open('employee_documents', d)}>
            <td><b>{d.title}</b></td><td className="mono">{d.document_number || '—'}</td><td>{d.issuing_authority || '—'}</td><td>{fmtDate(d.issue_date)}</td>
            <td>{fmtDate(d.expiry_date)}</td><td className="num">{daysUntil(d.expiry_date) ?? '—'}</td><td><Band date={d.expiry_date} /></td>
            <td>{d.is_verified ? <span className="xp-band valid">Verified</span> : <span className="xp-band d90">Pending</span>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Passport(x) {
  const passports = x.docs.filter(d => ['passport', 'visa'].includes(d.document_type));
  return (
    <>
      <Section icon="book-open" title="Expatriate Passport Details" hint="The passport currently held; expiry feeds the Document Expiry Dashboard.">
        <Grid>{['passport_number', 'passport_country', 'passport_expiry_date'].map(n => <Fld key={n} x={x} name={n} />)}</Grid>
      </Section>
      <Section icon="stamp" title="Nigerian Visa / STR Details">
        <Grid>{['visa_type', 'visa_expiry_date'].map(n => <Fld key={n} x={x} name={n} />)}</Grid>
      </Section>
      <Section icon="files" title="Passport & Visa Records" hint="Every passport and visa document held, newest first, with days to expiry."
               actions={<AddBtn x={x} res="employee_documents" label="Add Passport / Visa" defaults={{ document_type: 'passport', title: 'Passport' }} />}>
        <DocTable x={x} rows={passports} />
        <div className="xp-legend"><b>Expiry Legend:</b>
          {[['Valid', 'valid'], ['Expiring in 180 days', 'd180'], ['Expiring in 90 days', 'd90'], ['Expiring in 30 days', 'd30'], ['Expired', 'expired'], ['No Expiry', 'none']]
            .map(([l, b]) => <span key={b} className={`xp-band ${b}`}>{l}</span>)}
        </div>
      </Section>
    </>
  );
}

function Cerpac(x) {
  const e = x.e;
  const warnings = [];
  const soon = (d, what) => { const l = daysUntil(d); if (l !== null && l < 0) warnings.push(`${what} expired ${-l} days ago — renew immediately.`); else if (l !== null && l <= 90) warnings.push(`${what} is within ${l} days. Please initiate renewal process.`); };
  soon(e.cerpac_expiry_date, 'CERPAC expiry'); soon(e.visa_expiry_date, 'Visa / STR expiry'); soon(e.passport_expiry_date, 'Passport expiry');
  if (e.is_expatriate && !has(e.quota_slot_reference)) warnings.push('Employee is an expatriate but no quota approval reference is recorded.');
  if (e.is_expatriate && !has(e.cerpac_number)) warnings.push('Employee is an expatriate but no CERPAC number is recorded.');
  return (
    <>
      <Section icon="id-card" title="CERPAC / Residence Permit Details">
        <Grid>{['cerpac_number', 'cerpac_expiry_date'].map(n => <Fld key={n} x={x} name={n} />)}</Grid>
      </Section>
      <Section icon="briefcase" title="Expatriate Quota & Position Details">
        <Grid>
          <Fld x={x} name="quota_slot_reference" />
          <div className="field xp-actual"><span className="field-label">Actual Designation</span><b>{e.designation_title || '—'}</b></div>
          <div className="field xp-actual"><span className="field-label">Department</span><b>{e.department_name || '—'}</b></div>
        </Grid>
      </Section>
      <Section icon="files" title="Permit Documents" actions={<AddBtn x={x} res="employee_documents" label="Add Permit" defaults={{ document_type: 'cerpac', title: 'CERPAC' }} />}>
        <DocTable x={x} rows={x.docs.filter(d => ['cerpac', 'work_permit', 'residence_permit'].includes(d.document_type))} />
      </Section>
      {warnings.length > 0 && <div className="panel xp-warnings"><h3 className="panel-title"><AlertTriangle size={17} /> Warnings</h3><ul>{warnings.map(w => <li key={w}>{w}</li>)}</ul></div>}
    </>
  );
}

function Reporting(x) {
  const e = x.e;
  const manager = x.people.find(p => String(p.id) === String(e.reports_to_id));
  const reports = x.people.filter(p => String(p.reports_to_id) === String(e.id));
  return (
    <>
      <Section icon="git-fork" title="Reporting Line" hint="Line manager and direct reports">
        <Grid cols={2}><Fld x={x} name="reports_to_id" /></Grid>
      </Section>
      <Section icon="users" title="Reporting Structure">
        <div className="table-wrap"><table className="xp-table">
          <thead><tr><th>Role</th><th>Name</th><th>Employee ID</th><th>Designation</th><th>Department</th></tr></thead>
          <tbody>
            <tr><td><b>Line Manager</b></td><td>{manager ? <Link to={hrSubUrl('employee-360', { id: manager.id })}>{manager.full_name}</Link> : <span className="muted">Not assigned</span>}</td>
              <td className="mono">{manager?.employee_no || '—'}</td><td>{manager?.designation_title || '—'}</td><td>{manager?.department_name || '—'}</td></tr>
            {reports.map(p => (
              <tr key={p.id}><td>Direct Report</td><td><Link to={hrSubUrl('employee-360', { id: p.id })}>{p.full_name}</Link></td>
                <td className="mono">{p.employee_no}</td><td>{p.designation_title || '—'}</td><td>{p.department_name || '—'}</td></tr>
            ))}
          </tbody>
        </table></div>
      </Section>
    </>
  );
}

function Sites(x) {
  return (
    <Section icon="map-pin" title="Project Site Access" hint={`Primary site: ${x.e.site_name || '—'} · additional sites need HSE induction, medical fitness and PPE clearance`}
             actions={<AddBtn x={x} res="site_accesses" label="Grant Site Access" />}>
      <MiniTable res={RESOURCES.site_accesses} rows={x.permits} columns={['site_id', 'access_type', 'start_date', 'end_date', 'hse_induction_passed', 'medical_fitness_passed', 'ppe_issued', 'status']}
                 empty="No additional site access granted." />
    </Section>
  );
}

function Probation(x) {
  const e = x.e;
  const left = daysUntil(e.probation_end_date);
  return (
    <>
      <Section icon="hourglass" title="Probation Management" hint={e.employment_status === 'probation' ? (left !== null && left < 0 ? `Probation ended ${-left} day(s) ago — confirmation overdue` : `On probation${left !== null ? ` — ${left} day(s) left` : ''}`) : 'Not currently on probation'}>
        <Grid>
          <div className="field xp-actual"><span className="field-label">Start Date</span><b>{fmtDate(e.hire_date)}</b></div>
          <Fld x={x} name="probation_end_date" />
          <Fld x={x} name="confirmation_date" />
          <div className="field xp-actual"><span className="field-label">Status</span><b><StatusPill value={e.employment_status} /></b></div>
        </Grid>
        {e.employment_status === 'probation' && left !== null && left < 0 && <Note tone="warn">Probation has ended. Confirm the employee from Manage Employees (row menu → Activate) once the review is done.</Note>}
      </Section>
    </>
  );
}

function Family(x) {
  const e = x.e;
  return (
    <>
      <Section icon="siren" title="Emergency Contacts & Next of Kin" actions={x.canManage && <Link className="btn" to={hrSubUrl('add-employee', { id: e.id })}>Edit Contacts</Link>}>
        {e.emergency_contacts.length ? (
          <div className="table-wrap"><table className="xp-table">
            <thead><tr><th>Name</th><th>Relationship</th><th>Phone</th><th>Email</th><th>Address</th><th>Next of Kin</th></tr></thead>
            <tbody>{e.emergency_contacts.map(c => <tr key={c.id}><td><b>{c.contact_name}</b></td><td>{c.relationship_type}</td><td>{c.phone_primary}</td><td>{c.email || '—'}</td><td>{c.address || '—'}</td><td>{c.is_next_of_kin ? 'Yes' : 'No'}</td></tr>)}</tbody>
          </table></div>
        ) : <div className="empty">No emergency contacts on file.</div>}
      </Section>
      <Section icon="users" title="Dependants" hint="Family members relocating with or supported by the employee" actions={<AddBtn x={x} res="employee_dependents" label="Add Dependant" />}>
        <MiniTable res={RESOURCES.employee_dependents} rows={e.dependents} empty="No dependants on file." />
      </Section>
    </>
  );
}

const PERSON_DOCS = [['Passport', 'passport_expiry_date', 'passport_number'], ['Visa / STR', 'visa_expiry_date', null], ['CERPAC', 'cerpac_expiry_date', 'cerpac_number']];

function Expiry(x) {
  const [f, setF] = useState({ q: '', type: '', dept: '', site: '', doc: '', status: '', window: '' });
  const set = (k, v) => setF(o => ({ ...o, [k]: v }));
  const expats = x.people.filter(p => p.is_expatriate);
  const rows = useMemo(() => {
    const out = x.allDocs.map(d => ({ key: `d${d.id}`, p: x.byId[d.employee_id], type: titleCase(d.document_type), number: d.document_number, issue: d.issue_date, expiry: d.expiry_date, verified: d.is_verified }));
    for (const [id, detail] of Object.entries(x.expatDetails)) {
      for (const [t, dk, nk] of PERSON_DOCS) if (detail[dk]) out.push({ key: `${id}${t}`, p: x.byId[id], type: t, number: nk ? detail[nk] : null, issue: null, expiry: detail[dk], verified: 'Profile' });
    }
    return out.filter(r => r.p).map(r => ({ ...r, left: daysUntil(r.expiry), band: expiryBand(r.expiry) })).sort((a, b) => (a.left ?? 1e9) - (b.left ?? 1e9));
  }, [x.allDocs, x.byId, x.expatDetails]);
  const within = (r, kind, n = 60) => r.type === kind && r.left !== null && r.left >= 0 && r.left <= n;
  const missing = expats.filter(p => !x.expatDetails[p.id]?.passport_expiry_date || !x.expatDetails[p.id]?.cerpac_expiry_date);
  const kpis = [
    ['Employees with Expiring Documents', new Set(rows.filter(r => r.left !== null && r.left <= 60).map(r => r.p.id)).size, 'blue', 'users', 'Within 60 days or expired'],
    ['Total Expatriates', expats.length, 'purple', 'globe', 'Classified expatriate'],
    ['Expiring Passports', rows.filter(r => within(r, 'Passport')).length, 'amber', 'book-open', 'Within 60 days'],
    ['Expiring Visa / STR', rows.filter(r => within(r, 'Visa / STR')).length, 'orange', 'stamp', 'Within 60 days'],
    ['Expiring CERPAC', rows.filter(r => within(r, 'CERPAC')).length, 'cyan', 'id-card', 'Within 60 days'],
    ['Expired Documents', rows.filter(r => r.left !== null && r.left < 0).length, 'red', 'circle-x', 'Overdue'],
    ['Missing Documents', missing.length, 'rose', 'file-x', 'Expatriates without passport or CERPAC expiry'],
    ['Pending Verification', x.allDocs.filter(d => !d.is_verified).length, 'yellow', 'clock', 'Awaiting review'],
  ];
  const uniq = fn => [...new Set(rows.map(fn).filter(Boolean))].sort();
  const shown = rows.filter(r => {
    const p = r.p;
    if (f.q && ![p.full_name, p.employee_no, r.number].some(v => String(v || '').toLowerCase().includes(f.q.toLowerCase()))) return false;
    if (f.type && (f.type === 'Expatriate') !== !!p.is_expatriate) return false;
    if (f.dept && p.department_name !== f.dept) return false;
    if (f.site && p.site_name !== f.site) return false;
    if (f.doc && r.type !== f.doc) return false;
    if (f.status && r.band[0] !== f.status) return false;
    if (f.window && !(r.left !== null && r.left <= Number(f.window))) return false;
    return true;
  });
  const exportCSV = () => download('document-expiry.csv', toCSV([
    { label: 'Employee Code', get: r => r.p.employee_no }, { label: 'Employee Name', get: r => r.p.full_name }, { label: 'Employee Type', get: r => (r.p.is_expatriate ? 'Expatriate' : 'Local') },
    { label: 'Department', get: r => r.p.department_name }, { label: 'Project Site', get: r => r.p.site_name }, { label: 'Document Type', get: r => r.type },
    { label: 'Document Number', get: r => r.number }, { label: 'Issue Date', get: r => r.issue }, { label: 'Expiry Date', get: r => r.expiry },
    { label: 'Days Left', get: r => r.left ?? '' }, { label: 'Status', get: r => r.band[0] },
  ], shown));
  const sel = (k, text, opts) => <Select value={f[k]} onChange={ev => set(k, ev.target.value)}><option value="">{text}</option>{opts.map(o => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o}>{o}</option>))}</Select>;
  return (
    <>
      <div className="xp-kpis">{kpis.map(([l, v, c, i, s]) => (
        <div key={l} className={`em-kpi lc-${c}`}><span className="lc-icon"><Icon name={i} size={19} /></span><div><b>{v}</b><span>{l}</span><small>{s}</small></div></div>
      ))}</div>
      <div className="panel me-table-panel">
        <div className="toolbar em-filters">
          <div className="search em-search"><Search size={16} /><input placeholder="Employee name, code or document no." value={f.q} onChange={ev => set('q', ev.target.value)} /></div>
          {sel('type', 'All Types', ['Expatriate', 'Local'])}
          {sel('dept', 'All Departments', uniq(r => r.p.department_name))}
          {sel('site', 'All Sites', uniq(r => r.p.site_name))}
          {sel('doc', 'All Document Types', uniq(r => r.type))}
          {sel('status', 'All Statuses', ['Expired', 'Expiring in 30 days', 'Expiring in 90 days', 'Expiring in 180 days', 'Valid', 'No Expiry'])}
          {sel('window', 'Any Expiry Date', [['30', 'Within 30 days'], ['60', 'Within 60 days'], ['90', 'Within 90 days'], ['180', 'Within 180 days']])}
          <button className="btn ghost" onClick={() => setF({ q: '', type: '', dept: '', site: '', doc: '', status: '', window: '' })}>Clear Filters</button>
          <div className="spacer" />
          <button className="btn" onClick={exportCSV}><Download size={15} /> Export Report</button>
        </div>
        <p className="muted small em-count">Employee Document Expiry Report — showing {shown.length} of {rows.length} entries</p>
        <div className="table-wrap register-wrap">
          <table className="me-table register-table xp-table">
            <thead><tr><th>#</th><th>Employee Code</th><th>Employee Name</th><th>Type</th><th>Department</th><th>Project Site</th>
              <th>Document Type</th><th>Document No.</th><th>Issue Date</th><th>Expiry Date</th><th>Days Left</th><th>Status</th><th>Verified</th><th>Open</th></tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.key}>
                  <td>{i + 1}</td><td className="mono">{r.p.employee_no}</td><td><b>{r.p.full_name}</b></td><td>{r.p.is_expatriate ? 'Expatriate' : 'Local'}</td>
                  <td>{r.p.department_name || '—'}</td><td>{r.p.site_name || '—'}</td><td>{r.type}</td>
                  <td className="mono">{r.number || '—'}</td><td>{fmtDate(r.issue)}</td><td>{fmtDate(r.expiry)}</td>
                  <td className={`num ${r.left !== null && r.left < 0 ? 'neg' : ''}`}>{r.left ?? '—'}</td><td><span className={`xp-band ${r.band[1]}`}>{r.band[0]}</span></td>
                  <td>{r.verified === 'Profile' ? 'Profile' : r.verified ? 'Verified' : <span className="xp-band d90">Pending</span>}</td>
                  <td><button className="btn small" onClick={() => x.select(r.p.id, r.type === 'CERPAC' ? 'cerpac' : 'passport')}>Open</button></td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={14} className="empty">No documents match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// key -> [label, icon, renderer, needs an employee]
export const TABS = {
  classification: ['Expatriate Classification', 'badge-check', Classification, true],
  passport: ['Passport & Visa Records', 'book-open', Passport, true],
  cerpac: ['CERPAC & Quota', 'id-card', Cerpac, true],
  reporting: ['Reporting Line', 'git-fork', Reporting, true],
  sites: ['Project Site Access', 'map-pin', Sites, true],
  probation: ['Probation Management', 'hourglass', Probation, true],
  family: ['Family & Contact', 'users', Family, true],
  expiry: ['Document Expiry Dashboard', 'calendar-clock', Expiry, false],
};

export default function ExpatriateClassification() {
  const { canManage, notify, confirm } = useHR();
  const [params, setParams] = useSearchParams();
  const tab = TABS[params.get('tab')] ? params.get('tab') : 'classification';
  const id = params.get('id') || '';
  const go = (t, emp = id) => setParams({ ...(emp ? { id: String(emp) } : {}), ...(t !== 'classification' ? { tab: t } : {}) });

  const people = useLookup('employees');
  const [orig, setOrig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [docs, setDocs] = useState([]);
  const [permits, setPermits] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [expatDetails, setExpatDetails] = useState({});
  const [adding, setAdding] = useState(null);
  const [opened, setOpened] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // open the first expatriate when no employee is chosen
  useEffect(() => {
    if (!id && people?.length && TABS[tab][3]) {
      const first = people.find(p => p.is_expatriate) || people[0];
      setParams({ id: String(first.id), ...(tab !== 'classification' ? { tab } : {}) }, { replace: true });
    }
  }, [people, id, tab, setParams]);

  const loadEmployee = useCallback(() => {
    if (!id) return;
    setError('');
    hr.getEmployee(id).then(e => { const f = flatten(e); setOrig(f); setDraft(f); }).catch(err => setError(errorText(err)));
    hr.listEmployeeDocuments(id).then(setDocs).catch(() => setDocs([]));
    hr.listSitePermits({ employee_id: id }).then(setPermits).catch(() => setPermits([]));
  }, [id]);
  useEffect(() => { setOrig(null); setDraft(null); loadEmployee(); }, [loadEmployee]);

  // the expiry dashboard reads every document plus the expatriates' passport / visa / CERPAC dates
  useEffect(() => {
    if (tab !== 'expiry' || !people) return;
    hr.listDocuments().then(setAllDocs).catch(() => setAllDocs([]));
    Promise.all(people.filter(p => p.is_expatriate).map(p => hr.getEmployee(p.id).then(e => [p.id, e.expatriate_detail || {}]).catch(() => [p.id, {}])))
      .then(pairs => setExpatDetails(Object.fromEntries(pairs)));
  }, [tab, people]);

  const byId = useMemo(() => Object.fromEntries((people || []).map(p => [p.id, p])), [people]);
  const dirty = draft && orig ? [...EXPAT_KEYS, ...MASTER_KEYS].filter(k => !same(draft[k], orig[k])) : [];

  const save = async () => {
    setBusy(true);
    try {
      const pick = keys => Object.fromEntries(keys.filter(k => dirty.includes(k)).map(k => [k, draft[k] === '' ? null : draft[k]]));
      const expat = pick(EXPAT_KEYS), master = pick(MASTER_KEYS);
      if (Object.keys(expat).length) await hr.updateExpatriate(id, { is_expatriate: !!draft.is_expatriate, ...expat });
      if (Object.keys(master).length) await hr.updateEmployee(id, master);
      clearLookup('employees');
      notify('Changes saved');
      loadEmployee();
    } catch (err) { notify(errorText(err), 'bad'); }
    setBusy(false);
  };
  const select = async (emp, t = tab === 'expiry' ? 'classification' : tab) => {
    if (dirty.length && !(await confirm({ title: 'Unsaved changes', message: 'Discard your unsaved changes?', confirmLabel: 'Discard', danger: true }))) return;
    go(t, emp);
  };

  if (error) return <div className="error">{error}</div>;
  if (!people) return <Loader title="Loading…" subtitle="Fetching employees" />;

  const needsEmp = TABS[tab][3];
  const x = draft && {
    e: draft, people, byId, docs, permits, allDocs, expatDetails, canManage, notify, select, setTab: t => go(t),
    set: (k, v) => setDraft(d => ({ ...d, [k]: v })),
    add: (res, defaults) => setAdding({ res, defaults }),
    open: (res, row) => setOpened({ res, row }),
  };
  const expats = people.filter(p => p.is_expatriate), locals = people.filter(p => !p.is_expatriate);
  const addRes = adding && RESOURCES[adding.res];

  return (
    <div className="xp">
      <div className="tabs e360-tabs xp-tabs" role="tablist">
        {Object.entries(TABS).map(([k, [text, icon]]) => (
          <button key={k} role="tab" className={`nav-btn ${tab === k ? 'active' : ''}`} onClick={() => go(k)}><Icon name={icon} size={15} /> <span>{text}</span></button>
        ))}
      </div>

      <div className="e360-head xp-head">
        <div className="e360-head-left">
          <Link className="back-btn" title="Back to Employees" to={hrSubUrl('manage-employees')}><ArrowLeft size={17} /></Link>
          {needsEmp ? (
            <>
              <Select className="xp-switch" value={id} onChange={ev => select(ev.target.value)}>
                <optgroup label={`Expatriates (${expats.length})`}>{expats.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.employee_no})</option>)}</optgroup>
                <optgroup label={`Other employees (${locals.length})`}>{locals.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.employee_no})</option>)}</optgroup>
              </Select>
              {orig && <span className="xp-who"><b>{orig.designation_title || '—'}</b><small>{[orig.department_name, orig.personal_info?.nationality, orig.site_name].filter(Boolean).join(' · ')}</small></span>}
              {orig && <StatusPill value={orig.employment_status} />}
              {orig && <span className={`xp-chip lc-${orig.is_expatriate ? 'blue' : 'slate'}`}>{orig.is_expatriate ? 'Expatriate' : 'Local'}</span>}
            </>
          ) : <span className="back-label">All employees · {expats.length} expatriates</span>}
        </div>
        <div className="e360-head-actions">
          {needsEmp && dirty.length > 0 && <span className="xp-dirty">{dirty.length} unsaved change{dirty.length > 1 ? 's' : ''}</span>}
          {needsEmp && dirty.length > 0 && <button className="btn ghost" onClick={() => setDraft(orig)}><RotateCcw size={15} /> Discard</button>}
          <button className="btn" onClick={() => window.print()}><Printer size={15} /> Print</button>
          {needsEmp && canManage && <button className="btn primary" disabled={!dirty.length || busy} onClick={save}><Save size={15} /> {busy ? 'Saving…' : 'Save Changes'}</button>}
        </div>
      </div>

      {!people.length && <div className="panel empty">No employees yet — add employees first.</div>}
      {needsEmp && people.length > 0 && !x && <Loader title="Loading employee…" subtitle="Fetching expatriate details" />}
      {needsEmp && x && React.createElement(TABS[tab][2], x)}
      {!needsEmp && React.createElement(TABS[tab][2], x || { e: {}, people, byId, allDocs, expatDetails, select })}

      {adding && (
        <RecordForm title={`New ${addRes.label} — ${draft?.full_name || ''}`} initial={adding.defaults}
                    fields={addRes.fields.filter(f => f.form !== false && f.form !== 'edit' && f.name !== 'employee_id')}
                    onClose={() => setAdding(null)}
                    onSubmit={async payload => { await addRes.create({ ...adding.defaults, ...payload, employee_id: id }); notify(`${addRes.label} created`); setAdding(null); loadEmployee(); }} />
      )}
      {opened && (
        <RecordDetail res={RESOURCES[opened.res]} record={opened.row} onClose={() => setOpened(null)} onChanged={() => { setOpened(null); loadEmployee(); }} />
      )}
    </div>
  );
}
